import { db } from '../db/indexeddb'

/**
 * Convert a startDate to an ISO string for Supabase.
 * If startTime is set (e.g. "17:00"), combine date + time in local timezone.
 * Otherwise use noon UTC so the calendar date survives timezone conversion.
 */
function dateToISO(date: Date | null, time: string | null): string | null {
  if (!date) return null
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  if (time) {
    const [h, min] = time.split(':').map(Number)
    const local = new Date(date)
    local.setHours(h, min, 0, 0)
    return local.toISOString()
  }
  return `${y}-${m}-${d}T12:00:00.000Z`
}

/** @deprecated Use dateToISO instead */
function startDateToISO(date: Date | null, startTime: string | null): string | null {
  return dateToISO(date, startTime)
}
import {
  getCloudTasks, getCloudTaskIds, getCloudTasksByIds, getDeletedCloudTasks,
  createCloudTask, updateCloudTask, deleteCloudTask,
  getCloudProjects, getCloudProjectIds, getCloudProjectsByIds, getDeletedCloudProjects,
  createCloudProject, updateCloudProject, deleteCloudProject,
  getCloudSections, getCloudSectionIds, getCloudSectionsByIds, getDeletedCloudSections,
  createCloudSection, updateCloudSection, deleteCloudSection,
  getCloudTags, getCloudTagIds, getCloudTagsByIds, getDeletedCloudTags,
  createCloudTag, updateCloudTag, deleteCloudTag,
  getCloudGoals, getCloudGoalIds, getCloudGoalsByIds, getDeletedCloudGoals,
  createCloudGoal, updateCloudGoal, deleteCloudGoal,
} from '../supabase/queries'
import {
  CloudTaskSchema, CloudTaskPayloadSchema,
  CloudProjectSchema, CloudProjectPayloadSchema,
  CloudSectionSchema, CloudSectionPayloadSchema,
  CloudTagSchema, CloudTagPayloadSchema,
  CloudGoalSchema, CloudGoalPayloadSchema,
  type ValidatedCloudGoalPayload,
} from './sync-schemas'
import { resolveCloudProjectId, resolveCloudSectionId, resolveLocalProjectId, resolveLocalSectionId, buildLocalToCloudTagIdMap, buildCloudToLocalTagIdMap } from './sync-resolve'

/**
 * Pick a lifespace for a task pulled from cloud. Pre-PR-1 payloads may arrive
 * without `life_space` (stale client, or the column was added between pull
 * windows). Fallback mirrors the Dexie v16 upgrade: lookup via project,
 * otherwise `'personal'`.
 */
async function resolveTaskLifeSpace(
  remoteLifeSpace: 'personal' | 'work' | 'home' | null | undefined,
  localProjectId: string | null | undefined,
): Promise<'personal' | 'work' | 'home'> {
  if (remoteLifeSpace) return remoteLifeSpace
  if (localProjectId) {
    const project = await db.projects.get(localProjectId)
    if (project?.lifeSpace) return project.lifeSpace
  }
  return 'personal'
}
import type { PullDescriptor } from './sync-pull'
import type { PushDescriptor } from './sync-push'
import type { Task, Project, Section, Tag, Goal } from '../db/models'

// ─── Task ────────────────────────────────────────────────────────────────────

export const taskPullDescriptor: PullDescriptor<any> = {
  entityName: 'task',
  settingsKey: 'lastSyncTime',

  fetchRemote: (userId, since) => getCloudTasks(userId, since),
  fetchRemoteIds: (userId) => getCloudTaskIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudTasksByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudTasks(userId, since),

  safeParse: (raw) => CloudTaskSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.tasks,
  getAllLocal: () => db.tasks.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.tasks.filter(t => !t.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote, userId) => {
    const localProjectId = await resolveLocalProjectId(remote.project_id)
    const localSectionId = await resolveLocalSectionId(remote.section_id)
    const tagIdMap = await buildCloudToLocalTagIdMap()
    const localTags = (remote.tags ?? []).flatMap((cloudId: string) => {
      const local = tagIdMap[cloudId]
      return local ? [local] : []
    })
    return {
      id: crypto.randomUUID(),
      title: remote.title,
      description: remote.description ?? null,
      status: remote.completed ? 'completed' : 'todo',
      attention: remote.attention ?? null,
      category: 'other',
      lifeSpace: await resolveTaskLifeSpace(remote.life_space, localProjectId),
      projectId: localProjectId,
      sectionId: localSectionId,
      userId: remote.user_id,
      cloudId: remote.id,
      dueDate: remote.due_date ? new Date(remote.due_date) : null,
      dueTime: remote.due_time ?? null,
      duePeriod: remote.due_period ?? null,
      startDate: remote.start_date ? new Date(remote.start_date) : null,
      startTime: remote.start_time ?? null,
      startPeriod: remote.start_period ?? null,
      completedAt: remote.completed ? new Date(remote.updated_at) : null,
      estimatedDuration: remote.estimated_duration ?? null,
      actualDuration: null,
      durationMode: remote.duration_mode ?? null,
      tags: localTags,
      parentTaskId: null,
      order: remote.order,
      deletedAt: null,
      pausedAt: remote.paused_at ? new Date(remote.paused_at) : null,
      createdAt: new Date(remote.created_at),
      updatedAt: new Date(remote.updated_at),
      lastModified: new Date(remote.updated_at),
      synced: true,
      syncedAt: new Date(),
    }
  },

  buildLocalUpdate: async (remote, existing) => {
    const localProjectId = await resolveLocalProjectId(remote.project_id)
    const localSectionId = await resolveLocalSectionId(remote.section_id)
    const tagIdMap = await buildCloudToLocalTagIdMap()
    const localTags = (remote.tags ?? []).flatMap((cloudId: string) => {
      const local = tagIdMap[cloudId]
      return local ? [local] : []
    })
    return {
      title: remote.title,
      description: remote.description ?? null,
      status: remote.completed ? 'completed' : 'todo',
      completedAt: remote.completed ? new Date(remote.updated_at) : null,
      ...('life_space' in remote && remote.life_space
        ? { lifeSpace: remote.life_space }
        : {}),
      projectId: localProjectId,
      sectionId: localSectionId,
      tags: localTags,
      attention: remote.attention ?? null,
      estimatedDuration: remote.estimated_duration ?? null,
      durationMode: remote.duration_mode ?? null,
      ...('start_date' in remote ? { startDate: remote.start_date ? new Date(remote.start_date) : null } : {}),
      ...('start_time' in remote ? { startTime: remote.start_time ?? null } : {}),
      ...('start_period' in remote ? { startPeriod: remote.start_period ?? null } : {}),
      ...('due_date' in remote ? { dueDate: remote.due_date ? new Date(remote.due_date) : null } : {}),
      ...('due_time' in remote ? { dueTime: remote.due_time ?? null } : {}),
      ...('due_period' in remote ? { duePeriod: remote.due_period ?? null } : {}),
      ...('paused_at' in remote ? { pausedAt: remote.paused_at ? new Date(remote.paused_at) : null } : {}),
      order: remote.order,
      updatedAt: new Date(remote.updated_at),
    }
  },
}

export const taskPushDescriptor: PushDescriptor<Task> = {
  entityName: 'CloudTask',
  getLocal: (id) => db.getTask(id),
  getLocalCloudId: (t) => t.cloudId,
  getLocalId: (t) => t.id,
  table: db.tasks,

  canCreate: async (task) => {
    if (task.projectId) {
      const cloudProjectId = await resolveCloudProjectId(task.projectId)
      if (!cloudProjectId) return { defer: true }
    }
    return { defer: false }
  },

  canUpdate: async (task) => {
    if (task.projectId) {
      const cloudProjectId = await resolveCloudProjectId(task.projectId)
      if (!cloudProjectId) return { defer: true }
    }
    return { defer: false }
  },

  buildCreatePayload: async (task) => {
    const cloudProjectId = await resolveCloudProjectId(task.projectId)
    const cloudSectionId = await resolveCloudSectionId(task.sectionId)
    const tagIdMap = await buildLocalToCloudTagIdMap()
    const cloudTags = task.tags.flatMap(localId => {
      const cloud = tagIdMap[localId]
      return cloud ? [cloud] : []
    })
    const payload = CloudTaskPayloadSchema.parse({
      title: task.title,
      description: task.description,
      completed: task.status === 'completed',
      life_space: task.lifeSpace,
      project_id: cloudProjectId,
      section_id: cloudSectionId,
      order: task.order,
      tags: cloudTags,
      attention: task.attention,
      estimated_duration: task.estimatedDuration,
      duration_mode: task.durationMode,
      start_date: startDateToISO(task.startDate, task.startTime),
      start_time: task.startTime ?? null,
      start_period: task.startPeriod ?? null,
      due_date: dateToISO(task.dueDate, task.dueTime),
      due_time: task.dueTime ?? null,
      due_period: task.duePeriod ?? null,
      paused_at: task.pausedAt ? task.pausedAt.toISOString() : null,
      local_id: task.id,
    })
    // Strip nullable columns that may not exist in Supabase yet
    if (payload.section_id == null) delete payload.section_id
    if (payload.due_date == null) delete payload.due_date
    if (payload.due_time == null) delete payload.due_time
    if (payload.due_period == null) delete payload.due_period
    if (payload.paused_at == null) delete payload.paused_at
    return payload
  },

  buildUpdatePayload: async (task) => {
    const cloudProjectId = await resolveCloudProjectId(task.projectId)
    const cloudSectionId = await resolveCloudSectionId(task.sectionId)
    const tagIdMap = await buildLocalToCloudTagIdMap()
    const cloudTags = task.tags.flatMap(localId => {
      const cloud = tagIdMap[localId]
      return cloud ? [cloud] : []
    })
    const payload: Record<string, any> = CloudTaskPayloadSchema.parse({
      title: task.title,
      description: task.description,
      completed: task.status === 'completed',
      life_space: task.lifeSpace,
      project_id: cloudProjectId,
      section_id: cloudSectionId,
      order: task.order,
      tags: cloudTags,
      attention: task.attention,
      estimated_duration: task.estimatedDuration,
      duration_mode: task.durationMode,
      start_date: startDateToISO(task.startDate, task.startTime),
      start_time: task.startTime ?? null,
      start_period: task.startPeriod ?? null,
      due_date: dateToISO(task.dueDate, task.dueTime),
      due_time: task.dueTime ?? null,
      due_period: task.duePeriod ?? null,
      paused_at: task.pausedAt ? task.pausedAt.toISOString() : null,
      local_id: task.id,
    })
    if (payload.section_id == null) delete payload.section_id
    if (!task.deletedAt) payload.deleted_at = null
    // paused_at is always sent (including null) so un-pausing reaches Supabase
    return payload
  },

  createCloud: (userId, payload) => createCloudTask(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudTask(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudTask(cloudId, userId),
}

// ─── Project ─────────────────────────────────────────────────────────────────

export const projectPullDescriptor: PullDescriptor<any> = {
  entityName: 'project',
  settingsKey: 'lastProjectSyncTime',

  fetchRemote: (userId, since) => getCloudProjects(userId, since),
  fetchRemoteIds: (userId) => getCloudProjectIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudProjectsByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudProjects(userId, since),

  safeParse: (raw) => CloudProjectSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.projects,
  getAllLocal: () => db.projects.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.projects.filter(p => !p.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote, userId) => ({
    id: crypto.randomUUID(),
    name: remote.name,
    description: remote.description ?? null,
    status: remote.status,
    lifeSpace: remote.life_space,
    startDate: remote.start_date ? new Date(remote.start_date) : null,
    endDate: remote.end_date ? new Date(remote.end_date) : null,
    progress: remote.progress,
    order: remote.order,
    userId,
    cloudId: remote.id,
    isSystem: remote.is_system ?? false,
    systemKind: remote.system_kind ?? null,
    createdAt: new Date(remote.created_at),
    updatedAt: new Date(remote.updated_at),
    lastModified: new Date(remote.updated_at),
    synced: true,
    syncedAt: new Date(),
    deletedAt: null,
  }),

  buildLocalUpdate: async (remote) => ({
    name: remote.name,
    description: remote.description ?? null,
    status: remote.status,
    order: remote.order,
    isSystem: remote.is_system ?? false,
    systemKind: remote.system_kind ?? null,
    updatedAt: new Date(remote.updated_at),
  }),
}

export const projectPushDescriptor: PushDescriptor<Project> = {
  entityName: 'CloudProject',
  getLocal: (id) => db.getProject(id),
  getLocalCloudId: (p) => p.cloudId,
  getLocalId: (p) => p.id,
  table: db.projects,

  buildCreatePayload: async (project) => CloudProjectPayloadSchema.parse({
    name: project.name,
    description: project.description,
    status: project.status,
    life_space: project.lifeSpace,
    start_date: project.startDate?.toISOString() ?? null,
    end_date: project.endDate?.toISOString() ?? null,
    progress: project.progress,
    order: project.order,
    local_id: project.id,
    is_system: project.isSystem ?? false,
    system_kind: project.systemKind ?? null,
  }),

  buildUpdatePayload: async (project) => {
    const payload: Record<string, any> = CloudProjectPayloadSchema.parse({
      name: project.name,
      description: project.description,
      status: project.status,
      life_space: project.lifeSpace,
      start_date: project.startDate?.toISOString() ?? null,
      end_date: project.endDate?.toISOString() ?? null,
      progress: project.progress,
      order: project.order,
      local_id: project.id,
      is_system: project.isSystem ?? false,
      system_kind: project.systemKind ?? null,
    })
    if (!project.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudProject(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudProject(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudProject(cloudId, userId),
}

// ─── Section ─────────────────────────────────────────────────────────────────

export const sectionPullDescriptor: PullDescriptor<any> = {
  entityName: 'section',
  settingsKey: 'lastSectionSyncTime',

  fetchRemote: (userId, since) => getCloudSections(userId, since),
  fetchRemoteIds: (userId) => getCloudSectionIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudSectionsByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudSections(userId, since),

  safeParse: (raw) => CloudSectionSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.sections,
  getAllLocal: () => db.sections.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.sections.filter(s => !s.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote, userId) => {
    const localProjectId = await resolveLocalProjectId(remote.project_id)
    if (!localProjectId) return null // project not yet synced locally — skip silently
    return {
      id: crypto.randomUUID(),
      title: remote.title,
      projectId: localProjectId,
      userId,
      cloudId: remote.id,
      order: remote.order,
      collapsed: remote.collapsed,
      createdAt: new Date(remote.created_at),
      updatedAt: new Date(remote.updated_at),
      lastModified: new Date(remote.updated_at),
      synced: true,
      syncedAt: new Date(),
      deletedAt: null,
    }
  },

  buildLocalUpdate: async (remote) => ({
    title: remote.title,
    order: remote.order,
    collapsed: remote.collapsed,
    updatedAt: new Date(remote.updated_at),
  }),
}

export const sectionPushDescriptor: PushDescriptor<Section> = {
  entityName: 'CloudSection',
  getLocal: (id) => db.getSection(id),
  getLocalCloudId: (s) => s.cloudId,
  getLocalId: (s) => s.id,
  table: db.sections,

  canCreate: async (section) => {
    if (section.projectId) {
      const cloudProjectId = await resolveCloudProjectId(section.projectId)
      if (!cloudProjectId) return { defer: true }
    }
    return { defer: false }
  },

  canUpdate: async (section) => {
    if (section.projectId) {
      const cloudProjectId = await resolveCloudProjectId(section.projectId)
      if (!cloudProjectId) return { defer: true }
    }
    return { defer: false }
  },

  buildCreatePayload: async (section) => {
    const cloudProjectId = await resolveCloudProjectId(section.projectId)
    return CloudSectionPayloadSchema.parse({
      project_id: cloudProjectId,
      title: section.title,
      order: section.order,
      collapsed: section.collapsed,
      local_id: section.id,
    })
  },

  buildUpdatePayload: async (section) => {
    const cloudProjectId = await resolveCloudProjectId(section.projectId)
    const payload: Record<string, any> = CloudSectionPayloadSchema.parse({
      project_id: cloudProjectId,
      title: section.title,
      order: section.order,
      collapsed: section.collapsed,
      local_id: section.id,
    })
    if (!section.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudSection(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudSection(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudSection(cloudId, userId),
}

// ─── Tag ────────────────────────────────────────────────────────────────────

export const tagPullDescriptor: PullDescriptor<any> = {
  entityName: 'tag',
  settingsKey: 'lastTagSyncTime',

  fetchRemote: (userId, since) => getCloudTags(userId, since),
  fetchRemoteIds: (userId) => getCloudTagIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudTagsByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudTags(userId, since),

  safeParse: (raw) => CloudTagSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.tags,
  getAllLocal: () => db.tags.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.tags.filter(t => !t.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote, userId) => ({
    id: crypto.randomUUID(),
    name: remote.name,
    color: remote.color,
    description: remote.description ?? null,
    usageCount: remote.usage_count,
    userId: remote.user_id,
    cloudId: remote.id,
    deletedAt: null,
    createdAt: new Date(remote.created_at),
    updatedAt: new Date(remote.updated_at),
    lastModified: new Date(remote.updated_at),
    synced: true,
    syncedAt: new Date(),
  }),

  buildLocalUpdate: async (remote) => ({
    name: remote.name,
    color: remote.color,
    description: remote.description ?? null,
    usageCount: remote.usage_count,
    updatedAt: new Date(remote.updated_at),
  }),
}

export const tagPushDescriptor: PushDescriptor<Tag> = {
  entityName: 'CloudTag',
  getLocal: (id) => db.getTag(id),
  getLocalCloudId: (t) => t.cloudId,
  getLocalId: (t) => t.id,
  table: db.tags,

  buildCreatePayload: async (tag) => CloudTagPayloadSchema.parse({
    name: tag.name,
    color: tag.color,
    description: tag.description,
    usage_count: tag.usageCount,
    local_id: tag.id,
  }),

  buildUpdatePayload: async (tag) => {
    const payload: Record<string, any> = CloudTagPayloadSchema.parse({
      name: tag.name,
      color: tag.color,
      description: tag.description,
      usage_count: tag.usageCount,
      local_id: tag.id,
    })
    if (!tag.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudTag(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudTag(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudTag(cloudId, userId),
}

// ─── Goal ────────────────────────────────────────────────────────────────────
// A Goal is a standalone user-owned entity (no project/section dependency),
// so its sync wiring mirrors Tag — the simplest cloud entity.

export const goalPullDescriptor: PullDescriptor<any> = {
  entityName: 'goal',
  settingsKey: 'lastGoalSyncTime',

  fetchRemote: (userId, since) => getCloudGoals(userId, since),
  fetchRemoteIds: (userId) => getCloudGoalIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudGoalsByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudGoals(userId, since),

  safeParse: (raw) => CloudGoalSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.goals,
  getAllLocal: () => db.goals.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.goals.filter(g => !g.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote) => ({
    id: crypto.randomUUID(),
    title: remote.title,
    description: remote.description ?? null,
    lifeSpace: remote.life_space,
    userId: remote.user_id,
    cloudId: remote.id,
    deletedAt: null,
    createdAt: new Date(remote.created_at),
    updatedAt: new Date(remote.updated_at),
    lastModified: new Date(remote.updated_at),
    synced: true,
    syncedAt: new Date(),
  }),

  buildLocalUpdate: async (remote) => ({
    title: remote.title,
    description: remote.description ?? null,
    lifeSpace: remote.life_space,
    updatedAt: new Date(remote.updated_at),
  }),
}

export const goalPushDescriptor: PushDescriptor<Goal> = {
  entityName: 'CloudGoal',
  getLocal: (id) => db.getGoal(id),
  getLocalCloudId: (g) => g.cloudId,
  getLocalId: (g) => g.id,
  table: db.goals,

  buildCreatePayload: async (goal) => CloudGoalPayloadSchema.parse({
    title: goal.title,
    description: goal.description,
    life_space: goal.lifeSpace,
    local_id: goal.id,
  }),

  buildUpdatePayload: async (goal) => {
    const payload: ValidatedCloudGoalPayload & { deleted_at?: null } = CloudGoalPayloadSchema.parse({
      title: goal.title,
      description: goal.description,
      life_space: goal.lifeSpace,
      local_id: goal.id,
    })
    if (!goal.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudGoal(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudGoal(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudGoal(cloudId, userId),
}
